import { useTranslation } from 'react-i18next';
import {
  AlertIcon,
  ArrowLeftRightIcon,
  CheckCircleIcon,
  LinkIcon,
  RefreshIcon,
  XCircleIcon,
} from '@utils/icons';
import type { IGenbaSite, ITrusstorProject } from '@interfaces/IProjectMerge';
import { Button } from '@components/ui/Button/Button';
import { Spinner } from './PaneState';
import styles from './CenterBridge.module.css';

/** Bridge phase, mapped from the create mutation lifecycle by the orchestrator. */
export type BridgePhase = 'idle' | 'linking' | 'success' | 'conflict';

interface ICenterBridgeProps {
  genbaSel: IGenbaSite | null;
  trusstorSel: ITrusstorProject | null;
  phase: BridgePhase;
  /** The minted site id, set once creation succeeds. */
  newSiteId: string | null;
  /** The D-ORCA name being entered for the new site. */
  name: string;
  onNameChange: (value: string) => void;
  onCreate: () => void;
  onClear: () => void;
  onRetry: () => void;
}

/** The center seam where a Genba site + a Trusstor project + a D-ORCA name
    become one D-ORCA-owned site. Renders the dashed rail with two provenance
    nodes and a card that walks idle -> linking -> success / conflict. */
export function CenterBridge({
  genbaSel,
  trusstorSel,
  phase,
  newSiteId,
  name,
  onNameChange,
  onCreate,
  onClear,
  onRetry,
}: ICenterBridgeProps) {
  const { t } = useTranslation();
  const bothSelected = !!genbaSel && !!trusstorSel;
  const hasName = name.trim().length > 0;
  // "Armed" = everything the create needs is present (both upstreams + a name).
  const armed = bothSelected && hasName;
  const hasOne = !!genbaSel || !!trusstorSel;
  const railArmed = bothSelected && phase !== 'conflict';

  let iconCls = styles.iconIdle;
  let icon = <ArrowLeftRightIcon size={20} />;
  let leadMuted = false;
  let lead = t('projectMerge.bridge.selectPair');
  let hint: string | null = t('projectMerge.bridge.selectPairHint');

  if (phase === 'linking') {
    iconCls = styles.iconLinking;
    icon = <Spinner size={22} />;
    lead = t('projectMerge.bridge.linking');
    hint = t('projectMerge.bridge.linkingHint');
  } else if (phase === 'success') {
    iconCls = styles.iconSuccess;
    icon = <CheckCircleIcon size={22} />;
    lead = t('projectMerge.bridge.linked');
    hint = t('projectMerge.bridge.linkedHint');
  } else if (phase === 'conflict') {
    iconCls = styles.iconConflict;
    icon = <XCircleIcon size={22} />;
    lead = t('projectMerge.bridge.conflict');
    hint = null;
  } else if (bothSelected && !hasName) {
    leadMuted = true;
    lead = t('projectMerge.bridge.nameRequired');
    hint = t('projectMerge.bridge.nameRequiredHint');
  } else if (armed) {
    iconCls = styles.iconArmed;
    icon = <LinkIcon size={20} />;
    lead = t('projectMerge.bridge.readyToLink');
    hint = t('projectMerge.bridge.readyToLinkHint');
  } else if (hasOne) {
    leadMuted = true;
    lead = genbaSel ? t('projectMerge.bridge.selectTrusstor') : t('projectMerge.bridge.selectGenba');
    hint = t('projectMerge.bridge.selectOneEachHint');
  }

  return (
    <div className={styles.bridge}>
      <div className={styles.connector}>
        <div className={`${styles.rail} ${railArmed ? styles.railArmed : ''}`} />
        <div className={`${styles.node} ${styles.nodeL} ${genbaSel ? styles.nodeFilledL : ''}`} />
        <div className={`${styles.node} ${styles.nodeR} ${trusstorSel ? styles.nodeFilledR : ''}`} />
      </div>

      <div className={styles.card}>
        <div className={`${styles.icon} ${iconCls}`}>{icon}</div>
        <div className={styles.body}>
          <div className={`${styles.lead} ${leadMuted ? styles.leadMuted : ''}`}>{lead}</div>
          {hint ? <div className={styles.hint}>{hint}</div> : null}

          {hasOne && phase !== 'success' ? (
            <div className={styles.stagePair}>
              <div className={`${styles.stageSide} ${styles.stageGenba} ${genbaSel ? '' : styles.stageEmpty}`}>
                <div className={styles.stageKey}>
                  <span className={styles.pin} />
                  {t('projectMerge.bridge.stageGenba')}
                </div>
                {genbaSel ? (
                  <>
                    <div className={`${styles.stageVal} ${styles.stageValJp}`}>{genbaSel.name}</div>
                    <div className={styles.stageCode}>{genbaSel.number}</div>
                  </>
                ) : (
                  <div className={styles.stageNotSel}>{t('projectMerge.bridge.stageNotSelected')}</div>
                )}
              </div>

              <div className={styles.stageJoin}>
                <span className={styles.joinLine} />
                <LinkIcon size={12} />
                <span className={styles.joinLine} />
              </div>

              <div
                className={`${styles.stageSide} ${styles.stageTrusstor} ${trusstorSel ? '' : styles.stageEmpty}`}
              >
                <div className={styles.stageKey}>
                  <span className={styles.pin} />
                  {t('projectMerge.bridge.stageTrusstor')}
                </div>
                {trusstorSel ? (
                  <>
                    <div className={styles.stageVal}>{trusstorSel.name}</div>
                    <div className={styles.stageCode}>{trusstorSel.id}</div>
                  </>
                ) : (
                  <div className={styles.stageNotSel}>{t('projectMerge.bridge.stageNotSelected')}</div>
                )}
              </div>
            </div>
          ) : null}

          {bothSelected && phase !== 'success' ? (
            <div className={styles.nameField}>
              <label className={styles.nameLabel} htmlFor="merge-site-name">
                {t('projectMerge.bridge.nameLabel')}
              </label>
              <input
                id="merge-site-name"
                className={styles.nameInput}
                type="text"
                value={name}
                placeholder={t('projectMerge.bridge.namePlaceholder')}
                disabled={phase === 'linking'}
                onChange={(e) => onNameChange(e.target.value)}
              />
            </div>
          ) : null}

          {phase === 'success' && newSiteId ? (
            <div className={styles.stagePair}>
              <div className={`${styles.stageSide} ${styles.stageUnified}`}>
                <div className={styles.stageKeyUnified}>{t('projectMerge.bridge.unifiedSite')}</div>
                <div className={styles.stageCodeUnified}>{newSiteId}</div>
              </div>
            </div>
          ) : null}
        </div>

        {phase === 'conflict' ? (
          <div className={styles.err}>
            <AlertIcon size={14} />
            <span>{t('projectMerge.bridge.conflictBody')}</span>
          </div>
        ) : null}

        <div className={styles.actions}>
          {phase === 'conflict' ? (
            <>
              <Button variant="secondary" size="sm" block onClick={onRetry}>
                <RefreshIcon size={14} />
                {t('projectMerge.bridge.reloadAndRetry')}
              </Button>
              <Button variant="ghost" size="sm" block onClick={onClear}>
                {t('projectMerge.bridge.clearSelection')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                size="md"
                block
                disabled={!armed || phase === 'linking' || phase === 'success'}
                onClick={onCreate}
              >
                {phase === 'linking' ? <Spinner size={15} /> : <LinkIcon size={15} />}
                {t('projectMerge.bridge.createButton')}
              </Button>
              {hasOne && phase === 'idle' ? (
                <Button variant="ghost" size="sm" block onClick={onClear}>
                  {t('projectMerge.bridge.clearSelection')}
                </Button>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
